import { Session, SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useKeyValueStore, useRawLocalState } from './GlobalState.tsx';

export type SessionContextAPI = {
    // Imperative: call to open the flow and await the result
    getOrOpenSession: () => Promise<Session>;
    // Reactive session value. Use this only for reactive, asynchronous updates.
    session: Session | null;
    sessionValid: boolean; // whether the session is non-null and not expired
    // Real-time access to the user ID. Use this in real-time code paths.
    getUserId: () => string | undefined;

    // Sign out of the existing session.
    signOut: () => void;

    // UI state + controls for your modal
    isOpen: boolean;
    resolve: (user: Session) => void; // call on successful sign-in
    reject: (reason: Error) => void; // call on cancel / failure
};

const SessionContext = createContext<SessionContextAPI | undefined>(undefined);

export function SessionContextProvider({
    supabaseClient,
    children,
}: {
    supabaseClient: SupabaseClient;
    children: ReactNode;
}) {
    const initialLocalState = useRawLocalState();
    const store = useKeyValueStore();

    const [isOpen, setOpen] = useState(false);
    const [session, setSession] = useState<Session | null>(getInitialSession);
    const [sessionValid, setSessionValid] = useState<boolean>(true);
    const sessionRef = useRef<Session | null>(session);

    function getInitialSession() {
        return initialLocalState.session ? (JSON.parse(initialLocalState.session) as Session) : null;
    }

    // Keep the current promise and its resolvers in refs
    const currentPromiseRef = useRef<Promise<Session> | null>(null);
    const resolveRef = useRef<((session: Session) => void) | null>(null);
    const rejectRef = useRef<((reason?: any) => void) | null>(null);

    const cleanup = () => {
        setOpen(false);
        resolveRef.current = null;
        rejectRef.current = null;
        currentPromiseRef.current = null;
    };

    const getOrOpenSession = () => {
        console.info('Ensuring user is signed in...');

        if (session) {
            // The user has already authenticated.
            return Promise.resolve(session);
        }

        // If a login flow is already open, return the same in-flight promise
        if (currentPromiseRef.current) {
            return currentPromiseRef.current;
        }

        const p = new Promise<Session>((resolve, reject) => {
            resolveRef.current = (session: Session) => {
                resolve(session);
                setSession(session);
                sessionRef.current = session;
                cleanup();
            };
            rejectRef.current = (reason: Error) => {
                reject(reason);
                cleanup();
            };
            setOpen(true);
        });

        currentPromiseRef.current = p;
        return p;
    };

    const resolve = (session: Session) => resolveRef.current?.(session);
    const reject = (reason?: any) => rejectRef.current?.(reason);
    const signOut = () => {
        setSession(null);
        void supabaseClient.auth.signOut();
        sessionRef.current = null;
    };

    // Ensure we return the same object and consumers don't rerender unless isOpen or data change
    const value: SessionContextAPI = useMemo(
        () => ({
            getOrOpenSession,
            sessionValid,
            session,
            signOut,
            isOpen,
            resolve,
            reject,
            getUserId: () => sessionRef.current?.user.id,
        }),
        [isOpen, session, sessionValid]
    );

    useEffect(() => {
        void store.setValue('session', JSON.stringify(session));
    }, [store, session]);

    useEffect(() => {
        async function validateSession() {
            const supabaseSession = (await supabaseClient.auth.getSession()).data.session;
            if (!supabaseSession && sessionRef.current) {
                // User thinks they're signed in but the session is gone/expired
                setSessionValid(false);
            }
        }
        void validateSession();
    }, []);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useLoginSession(): SessionContextAPI {
    const ctx = useContext(SessionContext);
    if (!ctx) throw new Error('useLoginSession must be used within a SessionContextProvider');
    return ctx;
}
